<template>
  <div class="px-5 sm:px-6 lg:px-10 py-6">
    <!-- Header -->
    <div class="flex items-start justify-between mb-6">
      <h1 class="text-[22px] sm:text-[24px] leading-7 font-bold text-gray-900">
        Services
      </h1>
      <button
        type="button"
        class="inline-flex items-center gap-2 h-9 px-4 rounded-full font-bold text-white text-sm shadow-sm hover:opacity-95"
        :style="{ backgroundColor: '#5B9AC5' }"
        @click="openAdd"
      >
        + New Service
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loadingList" class="flex justify-center py-16">
      <svg class="animate-spin h-8 w-8 text-[#5B9AC5]" viewBox="0 0 24 24" fill="none">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
      </svg>
    </div>

    <!-- Error -->
    <div v-else-if="listError" class="text-center py-16 text-red-500 text-sm">
      {{ listError }}
      <button class="ml-3 underline text-[#5B9AC5]" @click="fetchServices">Retry</button>
    </div>

    <!-- Table -->
    <div v-else class="bg-white border border-gray-200 rounded-2xl overflow-hidden">
      <table class="w-full table-fixed">
        <thead>
          <tr class="text-left text-xs text-gray-400 font-semibold">
            <th class="p-4 w-1/4">ID</th>
            <th class="p-4 w-2/4 text-center">Service Name</th>
            <th class="p-4 w-1/4 text-center">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="svc in services" :key="svc.id" class="border-t hover:bg-gray-50">
            <td class="p-4 text-gray-700 font-medium">{{ svc.id }}</td>
            <td class="p-4 text-center text-gray-800 font-medium">{{ svc.name }}</td>
            <td class="p-4">
              <div class="flex justify-center gap-4">
                <button class="flex items-center gap-1 text-sm text-gray-700 hover:text-gray-900" @click="openEdit(svc)">
                  <svg viewBox="0 0 24 24" class="w-4 h-4">
                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25Zm18-11.5a1 1 0 0 0 0-1.41L18.66 3a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75L21 5.75Z" fill="currentColor"/>
                  </svg>
                  Edit
                </button>
                <button class="flex items-center gap-1 text-sm text-red-500 hover:text-red-600" @click="confirmDelete(svc)">
                  <svg viewBox="0 0 24 24" class="w-4 h-4" fill="currentColor">
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12ZM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4Z"/>
                  </svg>
                  Delete
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="services.length === 0">
            <td colspan="3" class="text-center py-10 text-gray-400">No services found.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Add / Edit Modal -->
    <transition name="fade">
      <div v-if="showModal" class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
        <div class="bg-white w-[92%] max-w-md rounded-2xl shadow p-6 relative">
          <button class="absolute right-3 top-3 text-gray-500 hover:text-gray-700" @click="closeModal">✕</button>
          <h3 class="text-lg font-bold text-center mb-5">
            {{ modalMode === 'add' ? 'Add New Service' : 'Edit Service' }}
          </h3>
          <form @submit.prevent="submit" class="space-y-4">
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">Service name</label>
              <input
                v-model="modalForm.name"
                type="text"
                class="w-full h-10 rounded-xl border border-gray-300 px-3 text-sm"
                placeholder="Enter service name"
              />
            </div>
            <button
              type="submit"
              class="w-full h-10 rounded-full text-white font-semibold shadow-sm hover:opacity-95"
              :style="{ backgroundColor: '#5B9AC5' }"
              :disabled="submitting"
            >
              {{ submitting ? 'Saving...' : (modalMode === 'add' ? 'Add' : 'Save') }}
            </button>
          </form>
        </div>
      </div>
    </transition>

    <!-- Delete Confirm Modal -->
    <transition name="fade">
      <div v-if="showDeleteModal" class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
        <div class="bg-white w-[92%] max-w-md rounded-2xl shadow p-6 relative">

          <!-- Warning icon -->
          <div class="flex justify-center mb-4">
            <div class="w-16 h-16 rounded-full border-4 border-orange-300 flex items-center justify-center">
              <span class="text-orange-400 text-3xl font-bold">!</span>
            </div>
          </div>

          <h3 class="text-xl font-bold text-center text-gray-800 mb-2">Delete Service?</h3>
          <p class="text-center text-gray-500 text-sm mb-6">
            "{{ deletingService?.name }}" will be permanently deleted.
          </p>

          <div class="flex gap-3">
            <button
              class="flex-1 h-10 rounded-full border border-gray-300 text-gray-700 font-semibold text-sm hover:bg-gray-50"
              @click="closeDeleteModal"
            >
              Cancel
            </button>
            <button
              class="flex-1 h-10 rounded-full bg-red-500 hover:bg-red-600 text-white font-semibold text-sm"
              :disabled="deleting"
              @click="doDelete"
            >
              {{ deleting ? 'Deleting...' : 'Delete' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import api from "@/api";
import Swal from "sweetalert2";

export default {
  name: "SuppliersServices",

  data() {
    return {
      services:    [],
      loadingList: false,
      listError:   null,

      showModal:  false,
      modalMode:  "add",
      submitting: false,
      editingId:  null,
      modalForm:  { name: "" },

      showDeleteModal:  false,
      deletingService:  null,
      deleting:         false,
    };
  },

  created() {
    this.fetchServices();
  },

  methods: {
    async fetchServices() {
      this.loadingList = true;
      this.listError   = null;
      try {
        const res  = await api.get("/accounting/supplier-services");
        const data = res.data?.data ?? res.data ?? [];
        this.services = Array.isArray(data) ? data : [];
      } catch (err) {
        this.listError = err?.response?.data?.message || "Failed to load services.";
      } finally {
        this.loadingList = false;
      }
    },

    openAdd() {
      this.modalMode = "add";
      this.editingId = null;
      this.modalForm = { name: "" };
      this.showModal = true;
    },

    openEdit(svc) {
      this.modalMode = "edit";
      this.editingId = svc.id;
      this.modalForm = { name: svc.name || "" };
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
    },

    async submit() {
      this.submitting = true;
      try {
        if (this.modalMode === "edit") {
          const res     = await api.put(`/accounting/supplier-services/${this.editingId}`, this.modalForm);
          const updated = res.data?.data ?? res.data?.service ?? res.data;
          if (updated && updated.id) {
            const idx = this.services.findIndex(s => s.id === this.editingId);
            if (idx !== -1) this.services.splice(idx, 1, updated);
            else await this.fetchServices();
          } else {
            await this.fetchServices();
          }
        } else {
          const res     = await api.post("/accounting/supplier-services", this.modalForm);
          const created = res.data?.data ?? res.data?.service ?? res.data;
          if (created && created.id) this.services.push(created);
          else await this.fetchServices();
        }

        this.closeModal();
        await Swal.fire({
          icon: 'success',
          title: this.modalMode === 'add' ? 'Service added successfully' : 'Service updated successfully',
          toast: true,
          position: 'top',
          timer: 1400,
          showConfirmButton: false,
        });
      } catch (err) {
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err?.response?.data?.message || err?.message || 'Failed to save service.',
          toast: true,
          position: 'top',
          timer: 2200,
          showConfirmButton: false,
        });
      } finally {
        this.submitting = false;
      }
    },

    confirmDelete(svc) {
      this.deletingService = svc;
      this.showDeleteModal = true;
    },

    closeDeleteModal() {
      this.showDeleteModal = false;
      this.deletingService = null;
    },

    async doDelete() {
      this.deleting = true;
      try {
        await api.delete(`/accounting/supplier-services/${this.deletingService.id}`);
        this.services = this.services.filter(s => s.id !== this.deletingService.id);
        this.closeDeleteModal();
        await Swal.fire({
          icon: 'success',
          title: 'Service deleted successfully',
          toast: true,
          position: 'top',
          timer: 1400,
          showConfirmButton: false,
        });
      } catch (err) {
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err?.response?.data?.message || err?.message || 'Failed to delete service.',
          toast: true,
          position: 'top',
          timer: 2200,
          showConfirmButton: false,
        });
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity .15s ease; }
.fade-enter-from,  .fade-leave-to      { opacity: 0; }
</style>
